import { useState, useRef, useEffect } from 'react';
import { Text, Input, Button, VStack, Field } from '@chakra-ui/react';
import { useBreakpointValue } from '@chakra-ui/react';
import { useImportSheet } from '@/contexts/ImportSheetContext';
import { RecipeClient } from '@/lib/recipeClient';
import { toaster } from '@/lib/toaster';
import { validateUrl } from '@/utils/importUtils';

export default function ImportUrlForm() {
  const { isOpen, startPolling, isPolling, closeExistingToast } = useImportSheet();
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // ssr:false required for focus management (CLAUDE.md)
  const isDesktop = useBreakpointValue({ base: false, lg: true }, { ssr: false });

  // Auto-focus on desktop when the sheet opens or this form mounts.
  // isOpen in deps ensures focus fires on reopen even when view was already 'url'.
  useEffect(() => {
    if (!isDesktop || !isOpen) return;
    const t = setTimeout(() => inputRef.current?.focus(), 150);
    return () => clearTimeout(t);
  }, [isDesktop, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!url.trim()) {
      setUrlError('Please enter a link');
      return;
    }
    if (!validateUrl(url.trim())) {
      setUrlError('Please enter a valid link');
      return;
    }

    setUrlError('');
    setIsSubmitting(true);

    try {
      closeExistingToast();
      const response = await RecipeClient.importFromUrl({ url: url.trim() });
      startPolling(response.jobId);
      setIsSubmitting(false);
    } catch {
      closeExistingToast();
      setIsSubmitting(false);
      setUrlError('Failed to submit import. Please check the link and try again.');
      toaster.create({
        title: 'Error submitting import',
        description: 'Please check the link and try again',
        type: 'error',
      });
    }
  };

  return (
    <VStack gap={0} align="stretch">
      <form onSubmit={handleSubmit}>
        <VStack gap={0} align="stretch">
          <Field.Root invalid={!!urlError}>
            <Input
              ref={inputRef}
              type="url"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                if (urlError) setUrlError('');
              }}
              placeholder="https://example.com/recipe"
              aria-label="Recipe link"
              size="md"
              bg="bg.surface"
              borderColor="border.muted"
              _hover={{ borderColor: 'accent.secondary' }}
              _focus={{
                borderColor: 'accent.emphasis',
                boxShadow: '0 0 0 1px var(--chakra-colors-accent-emphasis)',
              }}
              disabled={isSubmitting || isPolling}
              fontFamily="body"
            />
            {urlError && <Field.ErrorText mt={1}>{urlError}</Field.ErrorText>}
          </Field.Root>

          <Text fontFamily="body" fontSize="xs" fontWeight="300" color="text.muted" mt={1} mb={5}>
            BBC Good Food, NYT Cooking, Instagram, TikTok and more
          </Text>

          <Button
            type="submit"
            bg="accent.emphasis"
            color="white"
            _hover={{ bg: 'accent.emphasisHover' }}
            size="md"
            w="100%"
            loading={isSubmitting || isPolling}
            loadingText="Importing…"
            disabled={!url.trim()}
          >
            Import recipe
          </Button>
        </VStack>
      </form>
    </VStack>
  );
}
